import React, { useState, useMemo } from 'react';
import { useApp } from '../contexts/AppProvider';
import { Eye, Link2, Target, PieChart, Download, Sparkles, ChartNetwork, Library } from 'lucide-react';
import { SupersetLogo } from '../assets/supersetLogo'

import QuotasTrackingFeature from './features/QuotasTrackingFeature';
import SurveyPreviewFeature from './features/SurveyPreviewFeature';
import LinkGeneratorFeature from './features/LinkGeneratorFeature';
import ExportDataFeature from './features/ExportDataFeature';
import ChatbotFeature from './features/ChatbotFeature';
import DataVizFeature from './features/DataVizFeature';
import SurveyPerformanceMetrics from './features/SurveyPerformanceMetrics';
import AdHocDataViz from './features/AdHocDataViz';
import ResourcesLinks from './features/ResourcesLinks'; // Add this import

function hasFeature(features, featureName) {
	if (!Array.isArray(features) || !featureName) return false;
	return features.some(
	  (feature) => feature.featureName === featureName
	);
}

function permissionIsTrue(features, featureName) {
	if (!Array.isArray(features) || !featureName) return false;
  
	const target = featureName.toLowerCase();
  
	return features.some(
	  (feature) =>
		(feature.featureName || '').toLowerCase() === target &&
		feature.permission === true
	);
  }


// TODO remove TAB_CONFIG  and make it dynamic based on useApp.features list
// the only thing is that you still dont know how to make the mapping with the corresponding component
// Tab configuration
const TAB_CONFIG = [
  {
	id: 'preview_survey',
	labelKey: 'tabPreview',
	icon: Eye,
	component: SurveyPreviewFeature
  },
  {
	id: 'link_manager',
	labelKey: 'tabLinkManager',
	icon: Link2,
	component: LinkGeneratorFeature
  },
  {
	id: 'track_quotas',
	labelKey: 'tabQuotas',
	icon: Target,
	component: QuotasTrackingFeature
  },
  {
	id: 'survey_performance_metrics',
	labelKey: 'tabPerformanceMetrics',
	icon: ChartNetwork,
	component: SurveyPerformanceMetrics
  },
  {
	id: 'export_data',
	labelKey: 'tabData',
	icon: Download,
	component: ExportDataFeature
  },
  {
	id: 'data_viz',
	labelKey: 'tabDataViz',
	icon: PieChart,
	component: DataVizFeature
  },
  {
	id: 'ad_hoc_dataviz',
	labelKey: 'tabAdHocDataViz',
	icon: SupersetLogo,
	component: AdHocDataViz
  },
  {
	id: 'chat_survey',
	labelKey: 'tabChatbot',
	icon: Sparkles,
	component: ChatbotFeature
  },
  {
	id: 'resource_links', // Matches the feature_name in your database
	labelKey: 'tabResources',
	icon: Library, // Or FileText if Library isn't available
	component: ResourcesLinks
  }
];

export default function Tabs() {
  const { features, t } = useApp();
  const [activeTab, setActiveTab] = useState(null);

  // Filter tabs based on features config and set initial active tab
  const availableTabs = useMemo(() => {
	if (!features) return [];
	
	const tabs = TAB_CONFIG.filter((tab) => {
		return hasFeature(features, tab.id) && permissionIsTrue(features, tab.id) || tab.id === 'ad_hoc_dataviz'
  	});
	
	// Set first available tab as active if none selected
	if (tabs.length > 0 && !activeTab) {
	  setActiveTab(tabs[0].id);
	}
	
	return tabs;
  }, [features, activeTab]);

  // If no permissions loaded yet
  if (!features) {
	return null;
  }

  // If no tabs available
  if (availableTabs.length === 0) {
	return (
	  <div className="border-b" style={{ backgroundColor: 'var(--color-bg)' }}>
		<div className="max-w-7xl mx-auto px-6 py-4">
		  <p style={{ color: 'var(--color-muted)' }} className="text-sm">
			<strong>No features available with your current permissions and/or interface configuration.
			If something is not like it is supposed to be, please contact your administrator</strong>
		  </p>
		</div>
	  </div>
	);
  }

  return (
	<div style={{ backgroundColor: 'var(--color-bg)' }}>
	  <div className="mx-auto">
		<div className="flex flex-wrap items-center px-8 w-full">
		  {availableTabs.map((tab) => {
			const Icon = tab.icon;
			const isActive = activeTab === tab.id;
			
			return (
			  <button
				key={tab.id}
				onClick={() => setActiveTab(tab.id)}
				className={`
				  relative px-4 py-3 flex items-center justify-center gap-2 text-m font-medium
				  transition-all duration-200 flex-1
				`}
				style={{
				  color: isActive ? 'var(--color-primary)' : 'var(--color-text)',
				  backgroundColor: isActive ? 'color-mix(in srgb, var(--color-primary) 5%, transparent)' : '',
				}}
				onMouseEnter={(e) => {
				  if (!isActive) {
					e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-primary) 5%, transparent)';
				  }
				}}
				onMouseLeave={(e) => {
				  if (!isActive) {
					e.currentTarget.style.backgroundColor = 'transparent';
				  }
				}}
			  >
				<Icon className="h-4 w-4" />
				<span>{t(tab.labelKey)}</span>
				
				{/* Active indicator bar */}
				{isActive && (
				  <div
					className="absolute bottom-0 left-0 right-0 h-0.5"
					style={{ backgroundColor: 'var(--color-primary)' }}
				  />
				)}
			  </button>
			);
		  })}
	  </div>
		{/* 👇 Tab content area */}
		<div className="flex-1 overflow-auto">
		{(() => {
			const activeConfig = availableTabs.find((tab) => tab.id === activeTab);
			if (!activeConfig) return null;

			const ActiveComponent = activeConfig.component;
			return <ActiveComponent featureName={activeConfig.id} />;
		})()}
		</div>
		</div>
	</div>
  );
}

